import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { profileApi } from '@/lib/api/profile';
import { onProfileMutationSuccess } from '@/lib/profile-query';
import {
  WorkCollaborationPreferenceValues,
  type UpdateProfileDto,
  type WorkCollaborationPreference,
} from '@paladin-monorepo/common';
import { SuccessIndicator } from '@/components/ui/success-indicator';
import { cn } from '@/lib/utils';

export function WorkCollaborationPreferencesCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showSuccess, setShowSuccess] = useState(false);
  const hideTimeoutRef = useRef<number | null>(null);
  const [selected, setSelected] = useState<WorkCollaborationPreference[]>([]);
  const initialized = useRef(false);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profileApi.getProfile(),
  });

  useEffect(() => {
    if (!profile || initialized.current) return;
    initialized.current = true;
    setSelected(
      WorkCollaborationPreferenceValues.filter((p) =>
        profile.workCollaborationPreferences?.includes(p),
      ),
    );
  }, [profile]);

  const mutation = useMutation({
    mutationFn: (dto: UpdateProfileDto) => profileApi.updateProfile(dto),
    onSuccess: (data) => {
      setSelected(
        WorkCollaborationPreferenceValues.filter((p) =>
          data.workCollaborationPreferences?.includes(p),
        ),
      );
      onProfileMutationSuccess(queryClient, data);
      setShowSuccess(true);
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
      hideTimeoutRef.current = window.setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    },
    onError: () => {
      toast.error(t('profile.collaborationPreferences.saveError'));
    },
  });

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const toggle = (pref: WorkCollaborationPreference, checked: boolean) => {
    setSelected((prev) =>
      checked ? [...prev, pref] : prev.filter((value) => value !== pref),
    );
  };

  const save = () => {
    if (selected.length < 1) {
      toast.error(t('profile.collaborationPreferences.minOne'));
      return;
    }
    mutation.mutate({ workCollaborationPreferences: selected });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('profile.collaborationPreferences.title')}
          <SuccessIndicator visible={showSuccess} />
        </CardTitle>
        <CardDescription>{t('profile.collaborationPreferences.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div>{t('generic.loading')}</div>
        ) : (
          <>
            <div className="space-y-3">
              {WorkCollaborationPreferenceValues.map((pref) => (
                <label
                  key={pref}
                  className={cn(
                    'flex cursor-pointer items-center gap-3 rounded-md border border-border p-3',
                  )}
                >
                  <Checkbox
                    checked={selected.includes(pref)}
                    onCheckedChange={(checked) => toggle(pref, checked === true)}
                  />
                  <span className="text-sm">
                    {t(`profile.collaborationPreferences.options.${pref}`)}
                  </span>
                </label>
              ))}
            </div>
            <Button type="button" onClick={save} disabled={mutation.isPending}>
              {t('profile.collaborationPreferences.save')}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
